import React from 'react';
import { DateValue } from '@mantine/dates';
import { ComboboxItem, Select } from '@mantine/core';
import { HudIcon } from '@hud/web-components/Icons/HudIcons';

interface TimeSelectProps {
  value: DateValue;
  onChange(value: DateValue): void;
  onDropdownOpen?(): void;
  onDropdownClose?(): void;
  error?: boolean;
}

export function TimeSelect({ value, onChange, onDropdownOpen, onDropdownClose }: TimeSelectProps) {
  const options = generateHourItems(value ?? new Date());

  const selectedValue = value?.toISOString() ?? null;
  return (
    <Select
      placeholder="Pick value"
      data={options}
      renderOption={(item) => <div>{formatTimeFromISO(item.option.value)}</div>}
      value={selectedValue}
      onChange={(_value, option) => {
        if (_value && option) {
          onChange(new Date(option.value));
        }
      }}
      onDropdownOpen={onDropdownOpen}
      onDropdownClose={onDropdownClose}
      rightSection={<HudIcon size="14px" icon="menu.caretDown" />}
    />
  );
}

function generateHourItems(date: Date = new Date()): ComboboxItem[] {
  const items: ComboboxItem[] = [];
  const baseDate = new Date(date);

  // Reset the date to start of the day
  baseDate.setMinutes(0);
  baseDate.setSeconds(0);
  baseDate.setMilliseconds(0);

  const now = new Date();
  const isToday = baseDate.toDateString() === now.toDateString();

  for (let hour = 0; hour <= 24; hour++) {
    // Create a new date object for each hour
    const dateForHour = new Date(baseDate);
    dateForHour.setHours(hour);
    let minutes = '00';

    // also push 23:59 on the same day
    if (hour === 24) {
      dateForHour.setDate(dateForHour.getDate() - 1);
      dateForHour.setHours(23);
      dateForHour.setMinutes(59)
      minutes = '59';
    }

    // Format the label
    const month = (dateForHour.getMonth() + 1).toString().padStart(2, '0');
    const day = dateForHour.getDate().toString().padStart(2, '0');
    const hour12 = dateForHour.getHours() % 12 || 12;
    const period = dateForHour.getHours() < 12 ? 'AM' : 'PM';

    const label = `${day}/${month} • ${hour12.toString().padStart(2, '0')}:${minutes} ${period}`;
    const hourInMs = 60 * 60 * 1000;
    const newValue = dateForHour.toISOString();

    // check that we're not inserting duplicate values before adding
    // this can happen, for example, when daylight saving time changes
    if (items.find((item) => item.value === newValue)) {
      continue;
    }

    items.push({
      value: newValue,
      label,
      disabled: isToday && dateForHour.getTime() > now.getTime() + hourInMs, // disable future hours, but allow the next bucket that is still partial.
    });
  }

  return items;
}

export function formatTimeFromISO(isoString: string): string {
  const date = new Date(isoString);
  return date.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
}
